import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams, type Location } from 'react-router-dom';
import { Maximize2, X } from 'lucide-react';
import { Tooltip } from '@vd/ui';
import { TicketDetail } from './TicketDetail';
import { coverPageShortcuts } from '../../lib/keyboard';
import styles from './TicketModal.module.css';

/** What a board puts in the history entry it opens a ticket with. */
export type BackgroundState = { backgroundLocation: Location };

/** The background page a ticket was opened over, when it was (see `AppRouter`). */
export function backgroundOf(location: Location): Location | null {
  const state = location.state as Partial<BackgroundState> | null;
  return state?.backgroundLocation ?? null;
}

/**
 * A ticket opened from the board, over it (Jason, 18 Oct): the same ticket
 * view as its own page, in a large dialog, so a look, a comment or a change
 * never leaves the board. The address is the ticket's own, so a refresh, a
 * copied link or a new tab is the full page, and the expand button goes there.
 * Closing is going back: the board is where it was, scroll and filters too.
 *
 * Escape closes it only when nothing inside wants the key: not from a field
 * being typed in, not while one of the ticket's own dialogs is open, and not
 * when something inside already handled it.
 */
export function TicketModal() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const returnFocus = useRef<Element | null>(typeof document === 'undefined' ? null : document.activeElement);

  const close = useCallback(() => {
    const background = backgroundOf(location);
    // Opened from the board, so the board is the entry behind this one.
    if (background && window.history.state?.idx > 0) navigate(-1);
    else navigate(background ? `${background.pathname}${background.search}` : '/', { replace: true });
  }, [location, navigate]);

  const expand = () => navigate(`/tickets/${ticketId}${location.search}`, { replace: true });

  useEffect(() => {
    dialogRef.current?.focus({ preventScroll: true });
    const back = returnFocus.current;
    return () => {
      if (back instanceof HTMLElement && back.isConnected) back.focus({ preventScroll: true });
    };
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;
      // One of the ticket's own dialogs is open: Escape is that dialog's.
      const dialogs = document.querySelectorAll('[role="dialog"]');
      // A tour's box is a dialog too, but not the ticket's, and never holds Escape from it (TOADO-988).
      if (Array.from(dialogs).some((dialog) => dialog !== dialogRef.current && !dialog.hasAttribute('data-tour-box'))) return;
      event.preventDefault();
      close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [close]);

  // The page under it keeps its keys to itself meanwhile: its T, Enter and
  // [ are the ticket's while the ticket is open.
  useEffect(() => coverPageShortcuts(), []);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, []);

  if (!ticketId) return null;
  return createPortal(
    <div className={styles.backdrop} onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}>
      <div ref={dialogRef} className={styles.dialog} role="dialog" aria-modal="true" aria-label="Ticket" tabIndex={-1}
        data-testid="ticket-modal">
        <div className={styles.controls}>
          <Tooltip content="Open as a full page">
            <button type="button" className={styles.control} onClick={expand} aria-label="Open as a full page">
              <Maximize2 size={15} aria-hidden />
            </button>
          </Tooltip>
          <Tooltip content="Close (Esc)">
            <button type="button" className={styles.control} onClick={close} aria-label="Close">
              <X size={16} aria-hidden />
            </button>
          </Tooltip>
        </div>
        <div className={styles.body}>
          <TicketDetail ticketId={ticketId} inModal />
        </div>
      </div>
    </div>,
    document.body,
  );
}
